import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LaunchIcon from "@mui/icons-material/Launch";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import CalculateIcon from "@mui/icons-material/Calculate";
import PeopleIcon from "@mui/icons-material/People";
import FlightIcon from "@mui/icons-material/Flight";

export default function AmyloidConnectPage() {
  const features = [
    {
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      title: "Clinical Trial Search",
      description:
        "Search 150+ amyloidosis-specific trials with plain-English explanations and smart filtering by type and location.",
      color: "#667eea",
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 40 }} />,
      title: "Education Center",
      description:
        "Comprehensive educational resources covering AL, ATTR, cardiac, neurological, and renal amyloidosis.",
      color: "#764ba2",
    },
    {
      icon: <CalculateIcon sx={{ fontSize: 40 }} />,
      title: "Medical Calculators",
      description:
        "Clinical calculators and tools designed specifically for amyloidosis patients and providers.",
      color: "#667eea",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      title: "Provider Tools",
      description:
        "Patient management, trial matching, and analytics tools for healthcare providers.",
      color: "#764ba2",
    },
    {
      icon: <FlightIcon sx={{ fontSize: 40 }} />,
      title: "Integrated Travel Booking",
      description:
        "Book flights, hotels, and ground transport for trial visits directly from a matched trial — no separate platform or re-entering trial details.",
      color: "#667eea",
    },
  ];

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Live Product"
                color="success"
                sx={{ fontWeight: 700, mb: 3 }}
              />
              <Typography variant="h2" fontWeight={800} gutterBottom>
                AmyloidConnect
              </Typography>
              <Typography
                variant="h5"
                sx={{ opacity: 0.9, mb: 4, fontWeight: 400 }}
              >
                The most comprehensive clinical trials and education platform
                for the amyloidosis community.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LaunchIcon />}
                  onClick={() =>
                    window.open("https://www.amyloidconnect.com", "_blank")
                  }
                  sx={{
                    bgcolor: "white",
                    color: "#667eea",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  Launch App
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  borderRadius: 3,
                  p: 3,
                }}
              >
                {[
                  "150+ Clinical Trials",
                  "AL, ATTR, Cardiac Coverage",
                  "Integrated Travel Booking",
                  "Daily Updates",
                  "100% Free for Patients",
                  "HIPAA Compliant",
                  "Provider Dashboard",
                ].map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: "#a5f3d4" }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FEATURES */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Platform Features
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: feature.color, mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
            Join thousands of patients and providers using AmyloidConnect to
            navigate the clinical trial landscape.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<LaunchIcon />}
            onClick={() =>
              window.open("https://www.amyloidconnect.com", "_blank")
            }
            sx={{
              bgcolor: "white",
              color: "#667eea",
              fontWeight: 700,
              px: 5,
              py: 1.5,
              "&:hover": { bgcolor: "#f5f5f5" },
            }}
          >
            Launch AmyloidConnect — It's Free
          </Button>
        </Container>
      </Box>
    </Box>
  );
}